/**
 * **The attack forecast's shape** (`target-hover-forecast`): what one weapon or tech attack would
 * roll against one target, read before anything commits. `@lancer-vtt/rules`' `forecastAttack`
 * produces it from the same pre-roll stages `resolveSingleTarget` runs; {@link
 * import("./attackForecastMask").maskAttackForecast} masks it per viewer. The type lives here,
 * below both, because the mask is a domain concern and the rules package already depends on this
 * one.
 *
 * Names are raw ids, as on the recorded `to_hit` step ({@link AccuracyContribution.name}); the
 * render tier resolves them through `displaySourceName`.
 */

import type { AccuracyContribution } from "./steps";
import type { CombatantId, DamageType } from "./types";

/**
 * One term of the net accuracy. `kind` says where it came from:
 * - `intent`: the attack's own declared `--accuracy` (named `declared`, as the receipt prints it).
 * - `weapon`: the firing weapon's innate Accuracy/Difficulty (NPC weapons), named by weapon id.
 * - `modifier`: an overlay modifier S3 folded (a tag, a status, a talent, a conferred boon, an NPC feature).
 * - `cover`: the S2.5 cover term (`soft cover` / `hard cover`), carried by the target.
 *
 * `weapon` and `modifier` together are exactly the recorded step's `accuracyContributions`.
 */
export type AttackForecastContribution = AccuracyContribution & {
  kind: "intent" | "weapon" | "cover" | "modifier";
};

/** One damage instance's mitigation facts for one damage type. */
export type AttackForecastDamageType = {
  /** Index into `[weapon.damage, ...weapon.riders]`: 0 is the primary instance. */
  instance: number;
  type: DamageType;
  /** The Armor this instance subtracts: 0 when AP, Shredded, or a type Armor never applies to (burn, heat). */
  armor: number;
  /** Resistance halves it (target-side fold; Shredded suppresses it). A reaction such as Brace forecasts as declined. */
  resistant: boolean;
  immune: boolean;
  /** Exposed doubles it (physical types only). */
  doubled: boolean;
};

export type AttackForecastDamage = {
  ap: boolean;
  /** The target's effective Armor: 0 when Shredded, else its rating. */
  armor: number;
  /** A target-side modifier (Shredded) makes this attack ignore Armor and Resistance. */
  armorIgnored: boolean;
  /**
   * One entry per damage instance per possible type. An unanswered `variable` instance lists
   * every option; an answered one, or an override, lists one.
   */
  perType: AttackForecastDamageType[];
  /** The Exposed facet (doubles physical damage). */
  doubled: boolean;
  /** Sniper's Mark: a hit deals 1 structure damage instead. */
  substituteStructure: boolean;
  /** The target's current Overshield (0 when none). */
  overshield: number;
};

/**
 * One charge cost the attack would raise (`decisions/0211`): `amount` charges of the attacker's
 * Limited pool `pool` (its display name in `poolName`). `waivedOnCrit` names the replacement clause
 * that waives it when the first attack roll crits (Ammo Case III's Efficiency).
 */
export type AttackForecastCost = { pool: string; poolName: string; amount: number; waivedOnCrit?: string };

export type AttackForecast = {
  attackerId: CombatantId;
  targetId: CombatantId;
  /** The firing profile's id (the multi-profile child when one is selected). */
  weaponId: string;
  techAttack: boolean;
  /** In the receipt's order: declared, then the recorded contributions, then cover. Sums to {@link net}. */
  contributions: AttackForecastContribution[];
  /** Net accuracy (positive) or difficulty (negative), as the d6 pool will roll it. */
  net: number;
  /** The to-hit bonus added to the d20. */
  bonus: number;
  cover: "none" | "soft" | "hard";
  defence: {
    vs: "evasion" | "edef";
    value: number;
    /** The target's uncapped Evasion, when Stunned's cap lowered it. */
    cappedFrom?: number;
    /** The Stunned cap facet itself, when one applies (RAW, not a target stat). */
    evasionCap?: number;
  };
  /** `null` for an attack that deals no weapon damage (a tech attack, Ram, Grapple). */
  damage: AttackForecastDamage | null;
  riders: {
    /** Invisible's miss-chance facet: a pre-roll coin can whiff the attack outright. */
    missChance?: number;
    /** An advantage replacement (Particularize): the d20 rolls twice, keep the higher. */
    advantage: boolean;
    /** Target-side resource keys the hit consumes (Lock On). */
    consumeOnHit: string[];
    /** The charge costs the declared fire raises (an Ammo Case round), as settlement would pay them. Absent when none. */
    costs?: AttackForecastCost[];
  };
  warnings: string[];
  /** Exact P(hit) and P(crit) of the to-hit roll, before Invisible's coin and any reaction. */
  hitChance: { hit: number; crit: number };
};

/** An attack the forecast cannot describe (unknown combatant or weapon, a tech attack on an object, an area weapon). */
export type AttackForecastIllegal = { illegal: string };

export type AttackForecastResult = AttackForecast | AttackForecastIllegal;

/** The value a masked field shows: the viewer does not know it (the panel prints `??`). */
export const FORECAST_UNKNOWN = "unknown" as const;
export type ForecastUnknown = typeof FORECAST_UNKNOWN;

/**
 * {@link AttackForecast} as one viewer may see it. The target's own numbers (defence value,
 * Armor, per-type mitigation, Overshield) and everything derived from them (hit chance) become
 * {@link FORECAST_UNKNOWN} at class and loadout tier; contribution names follow the resolution
 * record's sentinel rule.
 */
export type MaskedAttackForecast = Omit<AttackForecast, "defence" | "damage" | "hitChance"> & {
  defence: Omit<AttackForecast["defence"], "value" | "cappedFrom"> & {
    value: number | ForecastUnknown;
    /** Absent when masked. */
    cappedFrom?: number;
  };
  damage:
    | (Omit<AttackForecastDamage, "armor" | "perType" | "overshield"> & {
        armor: number | ForecastUnknown;
        perType: AttackForecastDamageType[] | ForecastUnknown;
        overshield: number | ForecastUnknown;
      })
    | null;
  hitChance: AttackForecast["hitChance"] | ForecastUnknown;
};
